/**
 * The computer the control room is open on, for its keys and the analytics trail: a Mac (and an iPad with a keyboard)
 * says ⌘K and takes ⌘; Windows, Linux and the rest say Ctrl K and take Ctrl, as each system's own apps do.
 */
export type Os = 'mac' | 'windows' | 'linux' | 'chromeos' | 'ios' | 'android' | 'other';

export function osOf(platform: string, userAgent: string): Os {
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  if (/mac/i.test(platform) || /Macintosh/.test(userAgent)) return 'mac';
  if (/win/i.test(platform) || /Windows/.test(userAgent)) return 'windows';
  if (/CrOS/.test(userAgent)) return 'chromeos';
  if (/android/i.test(platform) || /Android/.test(userAgent)) return 'android';
  if (/linux/i.test(platform) || /Linux/.test(userAgent)) return 'linux';
  return 'other';
}

const nav = typeof navigator === 'undefined' ? null : navigator;
/** `navigator.userAgentData.platform` where the browser has it ("macOS", "Windows"), else the older `platform`. */
const platformName = (nav as (Navigator & { userAgentData?: { platform?: string } }) | null)?.userAgentData?.platform || nav?.platform || '';

export const os: Os = nav ? osOf(platformName, nav.userAgent) : 'other';
export const usesCommand = os === 'mac' || os === 'ios';
/** The command bar's keys, as this computer writes them. */
export const commandKeys = usesCommand ? '⌘K' : 'Ctrl K';
/** The key that takes a stop off, as this computer's keyboard labels it. */
export const deleteKey = usesCommand ? '⌫' : 'Del';
/** The modifier held for the command bar: ⌘ on a Mac, Ctrl elsewhere. */
export const commandHeld = (e: KeyboardEvent) => (usesCommand ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey);

/** The browser's family, for the analytics trail (a bucket, never the whole user agent). */
export function browserOf(userAgent: string): string {
  if (/Edg\//.test(userAgent)) return 'edge';
  if (/OPR\/|Opera/.test(userAgent)) return 'opera';
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (/Chrome\//.test(userAgent)) return 'chrome';
  if (/Safari\//.test(userAgent)) return 'safari';
  return 'other';
}
